import * as React from 'react';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import MuiMenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import {
  Box,
  Typography,
  Avatar,
  MenuList,
  ClickAwayListener,
  Popper,
  Button,
  Grow,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SignOutIcon,
  CaretUpIcon,
  CheckIcon,
  CopyIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../hooks/useAuth';
import theme from '../../theme';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { useUser } from '../../hooks/useUser';
import { routes } from '../../constants/routes';
import { useNavigate } from 'react-router-dom';

interface UserProfileOptionMenuProps {
  isopen?: boolean;
}

const MenuItem = styled(MuiMenuItem)({
  margin: '2px 0',
});

export default function UserProfileOptionMenu({
  isopen = false,
}: UserProfileOptionMenuProps) {
  const { copied, copy } = useCopyToClipboard();
  const [lastCopied, setLastCopied] = React.useState<string | null>(null);
  const { user, isLoading } = useUser();
  const { logout, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    try {
      logout();
      localStorage.removeItem('selectedMenus');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleCopyClick = (type: string) => {
    if (user) {
      if (type === 'apiKey') {
        copy(user?.apiKey[0]);
        setLastCopied('apiKey');
      } else if (type === 'tenantName') {
        copy(user?.tenantName);
        setLastCopied('tenantName');
      }
    }
  };

  React.useEffect(() => {
    if (!copied) {
      setLastCopied(null);
    }
  }, [copied]);

  return (
    <div>
      <Button
        variant="outlined"
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        sx={{
          display: 'flex',
          textTransform: 'none',
          my: 2,
          width: '100%',
          borderRadius: isopen ? '6px' : '50%',
          border: '1px solid #D9D9D9',
          p: isopen ? '8px' : '0',
          height: isopen ? 'auto' : '40px',
          minWidth: isopen ? 'auto' : '40px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isLoading ? (
          <Skeleton variant="circular" width={30} height={30} />
        ) : (
          <Avatar
            sizes="small"
            alt={user?.name || ''}
            src={user?.photo || ''}
            sx={{
              width: 28,
              height: 28,
              bgcolor: theme.palette.lightGray.main,
              color: theme.palette.lightGray.text,
            }}
          >
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
        )}
        {isopen && (
          <>
            <Box sx={{ mr: 'auto', ml: 1 }}>
              {isLoading ? (
                <Skeleton variant="text" width={100} height={20} />
              ) : (
                <Typography
                  sx={{
                    fontWeight: 500,
                    lineHeight: '16px',
                    color: theme.palette.custom.textBlue,
                    fontSize: '14px',
                  }}
                >
                  {user?.businessName || user?.name}
                </Typography>
              )}
            </Box>
            <CaretUpIcon color="#6B6F80" weight="bold" size={18} />
          </>
        )}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={isopen ? 'bottom-start' : 'left-start'}
        transition
        sx={{
          width: '230px',
          boxShadow: 1,
          border: '1px solid #E0E0E0',
          overflow: 'hidden',
          mb: 1,
          zIndex: 1300,
          borderRadius: '12px',
          position: 'fixed',
          ...(!isopen && {
            marginLeft: '75px',
          }),
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left center',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  sx={{
                    padding: '2px 0',
                  }}
                >
                  <MenuItem
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    onClick={() => handleCopyClick('apiKey')}
                  >
                    Copy API Key{' '}
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleCopyClick('apiKey')}
                    >
                      {copied && lastCopied === 'apiKey' ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                    </IconButton>
                  </MenuItem>
                  <Divider sx={{ mx: 2 }} />
                  <MenuItem
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => handleCopyClick('tenantName')}
                  >
                    <Tooltip title="Tenant Name" placement="right">
                      <Typography
                        sx={{
                          maxWidth: '160px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 'inherit',
                        }}
                      >
                        {user?.tenantName}
                      </Typography>
                    </Tooltip>
                    <IconButton size="small" sx={{ p: 0.5, flexShrink: 0 }}>
                      {copied && lastCopied === 'tenantName' ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                    </IconButton>
                  </MenuItem>
                  <Divider sx={{ mx: 2 }} />
                  <MenuItem
                    onClick={() => navigate(routes.userProfile)}
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <UserCircleIcon size={20} />
                    User Profile
                  </MenuItem>
                  <Divider sx={{ mx: 2 }} />
                  <MenuItem
                    onClick={handleLogout}
                    disabled={isAuthLoading}
                    sx={{
                      color: isAuthLoading ? 'text.disabled' : 'error.main',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    {isAuthLoading ? 'Logging out...' : 'Logout'}{' '}
                    <SignOutIcon
                      weight="bold"
                      size="18"
                      color={isAuthLoading ? '#999' : '#E74C3C'}
                    />
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}
